'use client';

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent as ReactDragEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import {
  FolderPlus,
  Upload,
  ChevronRight,
  ChevronDown,
  LayoutGrid,
  Rows3,
  Pencil,
  FolderInput,
  Share2,
  Eye,
  EyeOff,
  Globe,
  Download,
  Trash2,
  ExternalLink,
  Maximize2,
  Package,
  ArrowDownUp,
  Check,
  ListTree,
  AlignJustify,
  Users,
  AppWindow,
  FolderCog,
} from 'lucide-react';
import { openProjectDialog } from '@/components/casfs/project-launcher';
import { trackUpload } from '@/lib/content/transfers';
import { useReactFlow } from '@xyflow/react';
import { useT } from '@/components/i18n-provider';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { PanelSearchInput, panelChip } from './panel-ui';
import { haptic } from '@/lib/haptic';
import { downloadContent } from '@/lib/content/download-client';
import {
  useWorkspaceActions,
  useWorkspaceItems,
} from '@/components/workspace/store';
import { announceContentRenamed } from '@/components/workspace/item-label';
import { onWorkspaceItemsChanged } from '@/lib/workspaces/list-events';
import { WORKSPACE_PROJECT_MIME } from '@/lib/workspaces/types';
import {
  FileContextMenu,
  useFileContextMenu,
  type ContextMenuItem,
} from './file-context-menu';
import { ContentPreview, kindVisual, lucideMark, type ContentPreviewItem } from './content-preview';
import { resolveContentKind, parseWidgetIcon } from '@/lib/content/app-identity';
import {
  MediaLightbox,
  type MediaLightboxItem,
  type MediaLightboxType,
} from '@/components/media-lightbox';
import { ContentViewer } from '@/components/content-viewer';
import { openPlansDialog } from '@/components/plans-dialog-host';
import { subscribeContentCreate, consumePendingContentBrowse, subscribeContentBrowse } from './panel-events';
import { PortalFileDialog } from '@/components/workspace/portal-file-dialog';
import { ShareDialog } from '@/components/sharing/share-dialog';
import { VisibilityBadge, type Visibility, type VisibilitySource } from '@/components/sharing/visibility-badge';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ContentRow = ContentPreviewItem & {
  size?: number | null;
  parentId?: string | null;
  thumbnailPath?: string | null;
  previewUrl?: string | null;
  updatedAt?: string;
  linkVisibility?: 'private' | 'unlisted' | 'public' | null;
  shareToken?: string | null;
  effectiveVisibility?: Visibility;
  visibilitySource?: VisibilitySource;
  visibilitySourceName?: string | null;
  grantCount?: number;
};

type UserSummary = {
  storageUsed?: number;
  storageQuota?: number;
};

/** One row of `GET /api/content/[id]/open-in` — an app registered for the format.
 *  `mode` decides the pick's behaviour: `open` mounts the app ON the file (its
 *  registered document — Writer on a docx); `import` spawns a fresh scratch
 *  project of the app that imports the file (Video Studio on an mp4). */
type OpenInHandler = {
  id: string;
  widgetType: string | null;
  name: string;
  icon: string | null;
  isDefault: boolean;
  mode: 'open' | 'import';
};

const PAGE_LIMIT = 30;

/**
 * FILE rows (not folders, projects or apps) open in the media lightbox on
 * click — like post media — with the arrows walking the loaded listing.
 * Folders still browse in, projects/apps still open on the canvas.
 */
const isPreviewableRow = (row: ContentRow): boolean =>
  row.contentType !== 'folder' && row.contentType !== 'widget';

/** Row → lightbox slide type. Formats the lightbox can't render natively fall
 *  to `document`, which our renderBody covers (PDF/text sheet or a kind card). */
function lightboxTypeOf(row: ContentRow): MediaLightboxType {
  const ct = (row.contentType || '').toLowerCase();
  const mt = (row.mimeType || '').toLowerCase();
  if (ct === 'image' || mt.startsWith('image/')) return mt === 'image/gif' ? 'gif' : 'image';
  if (ct === 'video' || mt.startsWith('video/')) return 'video';
  if (ct === 'audio' || mt.startsWith('audio/')) return 'audio';
  if (ct === 'model3d' || ct === '3d' || mt.includes('gltf') || mt.includes('glb')) return 'model3d';
  return 'document';
}

/** Formats the lightbox shows as a document SHEET (ContentViewer). */
function sheetRenderable(mt: string): boolean {
  const base = (mt || '').split(';')[0].trim().toLowerCase();
  return (
    base === 'application/pdf' ||
    base === 'application/json' ||
    base.startsWith('text/') ||
    base.includes('javascript') ||
    base.includes('typescript')
  );
}

/** The registered app's mark for menu rows — lucide glyph, bundle art, or the
 *  generic app-window fallback. */
function handlerMark(h: OpenInHandler) {
  const ref = parseWidgetIcon(h.icon);
  const L = lucideMark(ref);
  if (L) return <L className="h-4 w-4" />;
  if (ref?.kind === 'url') {
    // eslint-disable-next-line @next/next/no-img-element
    return <img src={ref.url} alt="" className="h-4 w-4 rounded object-cover" />;
  }
  return <AppWindow className="h-4 w-4" />;
}

/**
 * Sentinel folder id for the Shared with me virtual folder — content other
 * people granted you, which has no place in your own folder tree. It behaves
 * like any other folder (pinned at the root, click to enter, breadcrumb to
 * leave) so the panel gains a destination rather than another row of chrome.
 */
const SHARED_ROOT = '__shared__';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Workspace file manager. Replaces the flat content list previously
 * rendered inside the My Library left-panel. Folder-aware, supports
 * upload + new folder + bulk delete + drag-to-folder + right-click
 * context menu. Each row is a drag source for the workspace canvas
 * (preserving the existing `application/x-workspace-item` payload).
 *
 * Network model:
 *   - One fetch per filter combination (parentId × q).
 *   - Infinite scroll within a filter via `page` + IntersectionObserver.
 *   - Optimistic local mutations for delete/rename/move so the panel
 *     never feels laggy; failures roll back.
 */
export function MyContentManager() {
  const t = useT();
  // --- nav state -----------------------------------------------------------
  // Personal-content folder navigation. The root entry has a null id (no
  // content row); descending names a real subfolder row. (Workspace
  // browsing now lives in the dedicated Workspaces tab.)
  const [folderStack, setFolderStack] = useState<
    { id: string | null; name: string }[]
  >([{ id: null, name: 'Root' }]);
  const top = folderStack[folderStack.length - 1];
  // Inside the shared branch the sentinel is never a real parentId — the query
  // below swaps it for the `owner=shared` scope.
  const parentId = top.id === SHARED_ROOT ? null : top.id;
  const inShared = folderStack.some((f) => f.id === SHARED_ROOT);
  const atSharedRoot = top.id === SHARED_ROOT;

  // --- filter state --------------------------------------------------------
  // We deliberately keep filtering minimal: a single search input that
  // accepts plain `q` or `@agent` tokens. The previous chip rail (All /
  // Images / Videos / Audio / Docs / 3D / Folders) added five rows of
  // chrome above the actual content and was used rarely — content is
  // sparse enough per-user that fuzzy search is faster. View-mode
  // (grid ↔ list) still toggles via a small icon next to the search.
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  // Sort order (server-side, so it holds across the paginated infinite scroll).
  // Default: most-recently modified first.
  const [sortMode, setSortMode] = useState<'recent' | 'newest' | 'oldest' | 'name' | 'size'>(
    'recent',
  );
  // Kind filter chips (registry-backed): everything / app documents /
  // workspaces / plain files. "Shared" is navigation (the virtual folder),
  // not a filter — its chip just enters that branch.
  const [kindFilter, setKindFilter] = useState<'all' | 'document' | 'workspace' | 'file'>('all');
  const [foldersFirst, setFoldersFirst] = useState(true);
  // Root browse mode: 'flat' = one cross-folder list (recency feed, nested work
  // surfaces); 'tree' = folders only at root, click to navigate in. Only affects
  // the root view — inside a folder both show that folder's children.
  const [layout, setLayout] = useState<'flat' | 'tree'>('flat');
  const [sortOpen, setSortOpen] = useState(false);

  // --- data state ----------------------------------------------------------
  const [rows, setRows] = useState<ContentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [user, setUser] = useState<UserSummary | null>(null);

  // --- selection -----------------------------------------------------------
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const clearSelection = useCallback(() => setSelected(new Set()), []);

  // --- folder DnD highlight ------------------------------------------------
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);

  // --- external-file drop (upload + project-bundle import) -----------------
  // Dragging a file from the OS onto the panel uploads it; a `.robproj.zip`
  // (see lib/content/project-bundle) is imported as a project instead. The
  // depth counter de-bounces the flicker from dragenter/leave over children.
  const [isFileDragging, setIsFileDragging] = useState(false);
  const dragDepth = useRef(0);

  // --- Move-to picker ------------------------------------------------------
  // Holds the row(s) being moved — one from the context menu, or the whole
  // selection from the bulk bar. When non-null the PortalFileDialog (move
  // mode) is rendered as a modal; confirming moves every row and clears this.
  const [moveRows, setMoveRows] = useState<ContentRow[] | null>(null);

  // --- ShareDialog --------------------------------------------------------
  // Holds the contentIds to share. Set from the row context menu (single id)
  // or the selection-bar Share button (the entire `selected` set). Cleared
  // on close. Bulk path also drives folder cascade — the dialog's
  // descendantCount notice surfaces when applicable.
  const [shareTargetIds, setShareTargetIds] = useState<string[] | null>(null);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // --- "+" dropdown menu (New folder / Upload) ----------------------------
  // New folder / Upload menu. The trigger "+" lives in the panel HEADER
  // (left-panel, same slot as every other view's create button); it fires
  // the `requestContentCreate` event which we subscribe to below.
  const [plusOpen, setPlusOpen] = useState(false);
  useEffect(() => subscribeContentCreate(() => setPlusOpen((v) => !v)), []);

  // --- context menu --------------------------------------------------------
  const ctx = useFileContextMenu<ContentRow>();

  // --- query construction --------------------------------------------------
  // The search box accepts the same tokens as /content:
  //   @username  → agentName filter (server-side)
  //   chat:slug  → not autocompleted here yet, plain `q` instead
  //   anything else → q
  const buildQuery = useCallback(
    (pageNum: number) => {
      const params = new URLSearchParams();
      // Shared branch: the root lists the grants themselves (`owner=shared`),
      // and descending into a shared folder needs `owner=any` so the server
      // resolves reachability through the folder rather than per child.
      // Searching the library root also widens to `any`: a search should find
      // everything you can see, including files people shared with you.
      const rootSearch = !!search.trim() && folderStack.length === 1;
      params.set(
        'owner',
        atSharedRoot ? 'shared' : inShared || rootSearch ? 'any' : 'me',
      );
      params.set('limit', String(PAGE_LIMIT));
      params.set('page', String(pageNum));
      // "Recently modified" is a poor lead for shared content: what matters is
      // when it reached you, which is the grant's addedAt (server `newest`).
      params.set('sort', atSharedRoot && sortMode === 'recent' ? 'newest' : sortMode);
      // Never surface widget bundle internals (their source .js/.mjs) in the
      // library — they belong to the widget, not the file browser.
      params.set('excludeBundleFiles', '1');
      if (foldersFirst && sortMode !== 'size') params.set('foldersFirst', '1');
      if (kindFilter !== 'all' && !inShared) params.set('kind', kindFilter);
      const q = search.trim();
      if (q.startsWith('@')) {
        params.set('agentName', q.slice(1));
      } else if (q) {
        params.set('q', q);
      }
      // Inside a folder: always restrict to that folder. At the ROOT, the
      // layout toggle decides: 'flat' omits parentId so the listing crosses
      // folder boundaries (a recency feed — nested projects surface), 'tree'
      // sets parentId=null so only root-level items show and folders are
      // navigated into. Searching always crosses folders.
      const flatFeed = !q && layout === 'flat';
      if (parentId) {
        params.set('parentId', parentId);
      } else if (atSharedRoot) {
        // Shared items live in someone else's tree, so they are never
        // root-level for you. The grant list IS the folder.
      } else if (!q && !flatFeed) {
        params.set('parentId', 'null');
      }
      return `/api/content?${params.toString()}`;
    },
    [parentId, search, sortMode, foldersFirst, layout, atSharedRoot, inShared, folderStack.length, kindFilter],
  );

  // Bumped when an item is added to the workspace canvas so the content
  // list + workspace cards refetch live (no page refresh).
  const [reloadNonce, setReloadNonce] = useState(0);
  useEffect(
    () => onWorkspaceItemsChanged(() => setReloadNonce((v) => v + 1)),
    [],
  );

  // --- initial / filter-change fetch ---------------------------------------
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setRows([]);
    setHasMore(true);
    setPage(1);
    fetch(buildQuery(1), { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.statusText)))
      .then((data) => {
        if (controller.signal.aborted) return;
        const list: ContentRow[] = (data?.items as ContentRow[]) ?? [];
        setRows(list);
        if (list.length < PAGE_LIMIT) setHasMore(false);
      })
      .catch((err: Error) => {
        if (err?.name === 'AbortError') return;
        setHasMore(false);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [buildQuery, reloadNonce]);

  // --- storage quota -------------------------------------------------------
  useEffect(() => {
    const controller = new AbortController();
    fetch('/api/user', { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (controller.signal.aborted || !data) return;
        setUser({ storageUsed: data?.storageUsed, storageQuota: data?.storageQuota });
      })
      .catch(() => {});
    return () => controller.abort();
  }, []);

  // --- shared-with-me ------------------------------------------------------
  // Drives the virtual folder row at the root. Nobody sharing with you means
  // no row at all, so the panel keeps its zero-filler-chrome root.
  const [sharedCount, setSharedCount] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    fetch('/api/content?owner=shared&limit=1', { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (controller.signal.aborted || !data) return;
        setSharedCount(Number(data.total) || 0);
      })
      .catch(() => {});
    return () => controller.abort();
  }, [reloadNonce]);

  // --- infinite scroll -----------------------------------------------------
  const loadMore = useCallback(async () => {
    if (loadingMore || loading || !hasMore) return;
    setLoadingMore(true);
    try {
      const next = page + 1;
      const res = await fetch(buildQuery(next));
      if (!res.ok) {
        setHasMore(false);
        return;
      }
      const data = await res.json();
      const list: ContentRow[] = (data?.items as ContentRow[]) ?? [];
      setRows((prev) => [...prev, ...list]);
      setPage(next);
      if (list.length < PAGE_LIMIT) setHasMore(false);
    } catch {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, loading, hasMore, page, buildQuery]);

  useEffect(() => {
    const root = scrollRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel || !hasMore) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            void loadMore();
            return;
          }
        }
      },
      { root, rootMargin: '0px 0px 200px 0px', threshold: 0 },
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [hasMore, loadMore]);

  // --- navigation ----------------------------------------------------------
  const enterFolder = (folder: ContentRow) => {
    haptic('light');
    setFolderStack((s) => [...s, { id: folder.id, name: folder.displayName }]);
    clearSelection();
  };
  const jumpToCrumb = (index: number) => {
    setFolderStack((s) => s.slice(0, index + 1));
    clearSelection();
  };
  const enterShared = () => {
    haptic('light');
    setFolderStack((s) => [...s, { id: SHARED_ROOT, name: t('workspace.panels.sharedWithMe') }]);
    clearSelection();
  };

  // Other panels (Workspaces "Browse files") hand off to Content scoped to a
  // folder. The request can fire before this component mounts (the tab switch
  // and the event race), so a pending stash is consumed on mount too.
  useEffect(() => {
    const browseTo = (target: { id: string; name: string; shared?: boolean }) => {
      // A folder the viewer does not own resolves through the shared branch
      // (`owner=any` + ancestor-grant reachability); their own goes direct.
      setFolderStack(
        target.shared
          ? [
              { id: null, name: 'Root' },
              { id: SHARED_ROOT, name: t('workspace.panels.sharedWithMe') },
              { id: target.id, name: target.name },
            ]
          : [{ id: null, name: 'Root' }, { id: target.id, name: target.name }],
      );
      setSearch('');
      clearSelection();
    };
    const pending = consumePendingContentBrowse();
    if (pending) browseTo(pending);
    return subscribeContentBrowse(browseTo);
  }, [clearSelection]);

  // Storage cleanup entry: the one view that makes freeing space possible —
  // every file, largest first, sizes visible.
  const manageStorage = useCallback(() => {
    setFolderStack([{ id: null, name: 'Root' }]);
    setSearch('');
    setKindFilter('file');
    setSortMode('size');
    setLayout('flat');
  }, []);

  // --- mutations -----------------------------------------------------------
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(buildQuery(1));
      const data = await res.json();
      const list: ContentRow[] = (data?.items as ContentRow[]) ?? [];
      setRows(list);
      setPage(1);
      setHasMore(list.length >= PAGE_LIMIT);
    } finally {
      setLoading(false);
    }
  }, [buildQuery]);

  const doDelete = async (id: string) => {
    const snapshot = rows;
    setRows((prev) => prev.filter((r) => r.id !== id));
    setSelected((s) => {
      const n = new Set(s);
      n.delete(id);
      return n;
    });
    const res = await fetch(`/api/content/${id}`, { method: 'DELETE' });
    if (!res.ok) {
      setRows(snapshot);
    }
  };

  const doBulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} item${selected.size === 1 ? '' : 's'}?`)) {
      return;
    }
    const ids = Array.from(selected);
    setRows((prev) => prev.filter((r) => !selected.has(r.id)));
    setSelected(new Set());
    await Promise.all(
      ids.map((id) =>
        fetch(`/api/content/${id}`, { method: 'DELETE' }).catch(() => null),
      ),
    );
  };

  const doRename = async (id: string, current: string) => {
    const next = window.prompt(t('workspace.panels.library.rename'), current);
    if (next === null) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === current) return;
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, displayName: trimmed } : r)),
    );
    const res = await fetch(`/api/content/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ displayName: trimmed }),
    }).catch(() => null);
    // Workspace labels (rail, Stage, overview, menus) read this tab's live
    // content cache, not these rows; only the announcement refreshes it, and
    // only for a name the server kept (item-label.ts, 2026-09-13).
    if (res?.ok) announceContentRenamed(id, trimmed);
  };

  const doMove = async (id: string, targetParentId: string | null) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
    const res = await fetch(`/api/content/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ parentId: targetParentId }),
    });
    if (!res.ok) {
      // Best-effort recovery: refresh the current view.
      await refresh();
    }
  };

  const doCycleVisibility = async (row: ContentRow) => {
    const order: Array<'private' | 'unlisted' | 'public'> = ['private', 'unlisted', 'public'];
    const current = row.linkVisibility ?? 'private';
    const next = order[(order.indexOf(current) + 1) % order.length];
    // Optimistic: the row badge renders effectiveVisibility (not linkVisibility), so update BOTH —
    // an explicit link set makes this node its own visibility source until the next refresh
    // (before this, the tag showed the STALE value until the panel was closed and reopened).
    setRows((prev) =>
      prev.map((r) =>
        r.id === row.id
          ? { ...r, linkVisibility: next, effectiveVisibility: next, visibilitySource: 'self' as const, visibilitySourceName: null }
          : r,
      ),
    );
    await fetch(`/api/content/${row.id}/link`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ visibility: next }),
    }).catch(() => {});
  };

  const doDownload = async (row: ContentRow) => {
    await downloadContent({
      contentId: row.id,
      filename: row.displayName,
      mimeType: row.mimeType,
    });
  };

  /** Download a folder as a portable `.robproj.zip` bundle (manifest + CAS
   *  blobs, no CRDT). The endpoint sets Content-Disposition, so a plain
   *  same-origin anchor click streams it with the right filename + cookie. */
  const doExportProject = (row: ContentRow) => {
    const a = document.createElement('a');
    a.href = `/api/content/${row.id}/export`;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // --- upload + new folder -------------------------------------------------
  const onUploadFiles = async (files: FileList | File[]) => {
    const list = Array.from(files);
    // Resumable, with progress in the transfer tray (CASFS-UI PF-4): a failure
    // is named there, so it is not repeated here.
    await Promise.all(
      list.map((file) =>
        trackUpload(file, { filename: file.name, mimeType: file.type, parentId: parentId ?? null, visibility: 'private' }).catch(() => null),
      ),
    );
    await refresh();
  };

  const onNewFolder = async () => {
    const name = window.prompt('Folder name');
    if (!name?.trim()) return;
    await fetch('/api/content/folder', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ displayName: name.trim(), parentId }),
    }).catch(() => {});
    await refresh();
  };

  // Files dropped from the OS: a `.robproj.zip` {t('workspace.panels.importsAsProject')} (into
  // the current folder); anything else uploads. A zip that isn't a valid
  // bundle falls back to a plain upload, so dropping any file just works.
  const handleExternalDrop = async (files: File[]) => {
    if (files.length === 0) return;
    const qs = parentId ? `?parentId=${encodeURIComponent(parentId)}` : '';
    const uploads: File[] = [];
    let importedAny = false;
    for (const f of files) {
      if (/\.zip$/i.test(f.name)) {
        const res = await fetch(`/api/content/import${qs}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/zip' },
          body: f,
        }).catch(() => null);
        if (res && res.ok) {
          importedAny = true;
          continue;
        }
        uploads.push(f); // not a project bundle → treat as a normal upload
      } else {
        uploads.push(f);
      }
    }
    if (uploads.length) await onUploadFiles(uploads); // onUploadFiles refreshes
    else if (importedAny) await refresh();
  };

  // Panel-level drop zone. Only engages for an OS file drag (`Files` in the
  // dataTransfer); internal folder-move drags (`application/x-content-move`)
  // are left to the per-row handlers and pass straight through.
  // Uploads always land in YOUR library, so the drop zone is off inside the
  // shared branch — dropping there would silently file the upload somewhere
  // the user isn't looking.
  const onPanelDragEnter = (e: ReactDragEvent) => {
    if (inShared) return;
    if (!e.dataTransfer.types.includes('Files')) return;
    dragDepth.current += 1;
    setIsFileDragging(true);
  };
  const onPanelDragOver = (e: ReactDragEvent) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };
  const onPanelDragLeave = (e: ReactDragEvent) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsFileDragging(false);
  };
  const onPanelDrop = (e: ReactDragEvent) => {
    if (inShared) return;
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    dragDepth.current = 0;
    setIsFileDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length) void handleExternalDrop(files);
  };

  // --- drag-to-canvas + drop-on-folder -------------------------------------
  const flow = useReactFlow();
  const { addItem, findFirstByRef } = useWorkspaceActions();
  const workspaceItems = useWorkspaceItems();

  const onRowDragStart = (e: ReactDragEvent, row: ContentRow) => {
    if (row.contentType === 'folder') {
      // We don't ship folder nodes onto the canvas. Folder drag is
      // reserved for moving folders BETWEEN folders.
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('application/x-content-move', JSON.stringify({ id: row.id }));
      e.dataTransfer.setData('text/plain', row.displayName);
      return;
    }
    e.dataTransfer.effectAllowed = 'copyMove';
    e.dataTransfer.setData(
      'application/x-workspace-item',
      JSON.stringify({
        type: 'content',
        refId: row.id,
        cache: {
          displayName: row.displayName,
          contentType: row.contentType,
          mimeType: row.mimeType,
        },
      }),
    );
    // Always present a move payload too so dropping ONTO a folder
    // moves the file rather than spawning a node.
    e.dataTransfer.setData('application/x-content-move', JSON.stringify({ id: row.id }));
    e.dataTransfer.setData('text/plain', row.displayName);
  };

  const onFolderDragOver = (e: ReactDragEvent, folderId: string) => {
    if (e.dataTransfer.types.includes('application/x-content-move')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setDropTargetId(folderId);
    }
  };
  const onFolderDragLeave = (folderId: string) => {
    setDropTargetId((cur) => (cur === folderId ? null : cur));
  };
  const onFolderDrop = async (e: ReactDragEvent, folder: ContentRow) => {
    setDropTargetId(null);
    const raw = e.dataTransfer.getData('application/x-content-move');
    if (!raw) return;
    try {
      const { id } = JSON.parse(raw) as { id: string };
      if (id === folder.id) return;
      e.preventDefault();
      await doMove(id, folder.id);
    } catch {
      /* malformed payload */
    }
  };

  /** A project folder (ADR-0022) carries `metadata.projectMime` → it OPENS WITH
   *  its associated editor (server-resolved via resolveContentHandler at `/w/`),
   *  rather than browsing into the raw files. */
  const projectMimeOf = (row: ContentRow): string | null => {
    if (row.contentType !== 'folder') return null;
    const m = (row.metadata ?? null) as Record<string, unknown> | null;
    const pm = m && typeof m.projectMime === 'string' ? m.projectMime : null;
    // A WORKSPACE is itself a content folder (ADR-0022) and carries a
    // projectMime, but there is no inline editor for a whole workspace. Treat it
    // as a plain folder so a click BROWSES IN (to its saved docs) instead of
    // trying to open it as an app — which hung forever in "Loading widget…":
    // no widget handles the workspace mime, so `widgetSpec` never resolves and
    // ContentNode sits in its project-folder loading panel indefinitely.
    if (pm === WORKSPACE_PROJECT_MIME) return null;
    return pm;
  };
  const openWithWidget = (row: ContentRow) =>
    window.open(`/w/${row.id}`, '_blank', 'noopener');

  const focusOrAdd = async (row: ContentRow) => {
    // A PLAIN folder browses in. A PROJECT folder (ADR-0022) is an app:
    // open it as a workspace instance on the canvas (focus the existing
    // instance if it's already there), NOT a standalone `/w/` tab. The
    // canvas mounts the editor inline, bound to this folder as its project.
    if (row.contentType === 'folder' && !projectMimeOf(row)) {
      enterFolder(row);
      return;
    }
    const existing = findFirstByRef(row.id);
    if (existing) {
      flow.setCenter(
        existing.state.x + existing.state.width / 2,
        existing.state.y + existing.state.height / 2,
        { zoom: flow.getZoom(), duration: 280 },
      );
      return;
    }
    await addItem({
      type: 'content',
      refId: row.id,
      state: {
        cache: {
          displayName: row.displayName,
          contentType: row.contentType,
          mimeType: row.mimeType,
          // Identity for the dock/taskbar: without these the puck falls back
          // to the anonymous cube and the hover card says "App" for a design.
          projectMime: projectMimeOf(row) ?? undefined,
          previewUrl:
            row.previewUrl ??
            (row.thumbnailPath ? `/api/content/${row.id}/preview` : undefined),
        },
      },
    }).catch(() => {});
  };

  const onCanvasCount = useCallback(
    (refId: string) => workspaceItems.filter((it) => it.refId === refId).length,
    [workspaceItems],
  );

  // --- lightbox preview + "Open in" ---------------------------------------
  // Clicking a FILE opens it in the media lightbox (like post media); adding
  // to the canvas moved into the explicit actions ("Add to workspace" in the
  // lightbox menu + context menu). `previewId` keys into the previewable
  // subset of the CURRENT listing, so the arrows walk exactly what the list
  // shows, in its sort order, and survive a background refetch.
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [openInMenu, setOpenInMenu] = useState(false);
  const [handlersById, setHandlersById] = useState<Record<string, OpenInHandler[]>>({});
  const handlersRequested = useRef(new Set<string>());

  const previewRows = useMemo(() => rows.filter(isPreviewableRow), [rows]);
  const previewIndex = useMemo(
    () => (previewId ? previewRows.findIndex((r) => r.id === previewId) : -1),
    [previewId, previewRows],
  );
  const previewItems = useMemo<MediaLightboxItem[]>(
    () =>
      previewRows.map((row) => ({
        url: `/api/content/${row.id}`,
        type: lightboxTypeOf(row),
        contentId: row.id,
        mimeType: row.mimeType,
        filename: row.displayName,
        thumbnailUrl:
          row.previewUrl ??
          (row.thumbnailPath ? `/api/content/${row.id}/preview` : null),
      })),
    [previewRows],
  );

  /** Fetch the registered apps for a row's format, once per id. */
  const loadHandlers = useCallback((rowId: string) => {
    if (handlersRequested.current.has(rowId)) return;
    handlersRequested.current.add(rowId);
    void fetch(`/api/content/${rowId}/open-in`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { handlers?: OpenInHandler[] } | null) => {
        if (j && Array.isArray(j.handlers)) {
          setHandlersById((cur) => ({ ...cur, [rowId]: j.handlers as OpenInHandler[] }));
        } else {
          handlersRequested.current.delete(rowId); // transient failure → retryable
        }
      })
      .catch(() => handlersRequested.current.delete(rowId));
  }, []);

  /** Row click: files preview in the lightbox; everything else keeps the old
   *  routing (folders browse in, projects/apps open on the canvas). Plain
   *  function like `focusOrAdd` — it closes over per-render state. */
  const openRow = (row: ContentRow) => {
    if (isPreviewableRow(row)) {
      loadHandlers(row.id);
      setOpenInMenu(false);
      setPreviewId(row.id);
      return;
    }
    void focusOrAdd(row);
  };

  /** "Open in <app>". Two behaviours by the handler's registered role:
   *
   *  - `open` (Writer on a docx): mount the app ON the file — a content item
   *    pinned via `state.openWith`; ContentNode mounts the app exactly like a
   *    project folder's editor and identity stays the file.
   *  - `import` (Video Studio on an mp4): spawn a FRESH scratch instance of
   *    the app (a normal catalog spawn → its own per-instance project room)
   *    with `state.importContentId` telling it to pull the file in on boot.
   *    Native projects stay exclusive to their app; each pick gets its own
   *    new project, never a shared per-file document. */
  const openWithApp = async (row: ContentRow, h: OpenInHandler) => {
    setPreviewId(null);
    setOpenInMenu(false);
    const existing = workspaceItems.find((it) =>
      h.mode === 'import'
        ? it.refId === h.id && it.state?.importContentId === row.id
        : it.refId === row.id && it.state?.openWith === h.id,
    );
    if (existing) {
      flow.setCenter(
        existing.state.x + existing.state.width / 2,
        existing.state.y + existing.state.height / 2,
        { zoom: flow.getZoom(), duration: 280 },
      );
      return;
    }
    if (h.mode === 'import') {
      await addItem({
        type: 'content',
        refId: h.id,
        state: {
          importContentId: row.id,
          cache: { displayName: h.name, contentType: 'widget' },
        },
      }).catch(() => {});
      return;
    }
    await addItem({
      type: 'content',
      refId: row.id,
      state: {
        openWith: h.id,
        cache: {
          displayName: row.displayName,
          contentType: row.contentType,
          mimeType: row.mimeType,
          previewUrl:
            row.previewUrl ??
            (row.thumbnailPath ? `/api/content/${row.id}/preview` : undefined),
        },
      },
    }).catch(() => {});
  };

  // Right-clicking a file warms its "Open in" list so the menu entries are
  // there by the time the eye reaches them (one tiny fetch, cached per id).
  useEffect(() => {
    const t = ctx.target;
    if (t && isPreviewableRow(t)) loadHandlers(t.id);
  }, [ctx.target, loadHandlers]);

  // --- context menu items --------------------------------------------------
  const buildMenuItems = (row: ContentRow): ContextMenuItem[] => {
    const isFolder = row.contentType === 'folder';
    const projMime = projectMimeOf(row);
    const v = row.linkVisibility ?? 'private';
    // Everything in the shared branch belongs to someone else: renaming,
    // moving, re-sharing and deleting are all the owner's to do, and the
    // server would reject them anyway. Offer only what a recipient can do.
    const readOnly = inShared;
    const isFile = isPreviewableRow(row);
    // Registered openers for this format ("Open in …"), warmed by the
    // ctx.target effect above; empty until the fetch lands.
    const openInItems: ContextMenuItem[] = isFile
      ? (handlersById[row.id] ?? []).map((h, i, arr) => ({
          id: `open-in-${h.id}`,
          label: t('workspace.panels.library.openIn', { app: h.name }),
          icon: handlerMark(h),
          onSelect: () => void openWithApp(row, h),
          separatorAfter: i === arr.length - 1,
        }))
      : [];
    const items: ContextMenuItem[] = [
      // Files PREVIEW on click now; the menu leads with the same action.
      ...(isFile
        ? [{
            id: 'preview',
            label: t('workspace.panels.library.preview'),
            icon: <Eye className="h-4 w-4" />,
            onSelect: () => openRow(row),
          } as ContextMenuItem]
        : []),
      {
        id: 'open',
        // A project folder OPENS as an app instance on the canvas; a plain
        // folder browses in; a file adds to the canvas. (focusOrAdd routes
        // all three.)
        label: projMime ? t('workspace.panels.library.addToWorkspace') : isFolder ? t('workspace.panels.openFolder') : t('workspace.canvas.actions.addToCanvas'),
        icon: projMime ? <ExternalLink className="h-4 w-4" /> : isFolder ? <FolderInput className="h-4 w-4" /> : <ExternalLink className="h-4 w-4" />,
        onSelect: () => void focusOrAdd(row),
      },
      ...openInItems,
      // A project folder's files, versions, backup and sharing in one place:
      // the portal's Project dialog (CASFS-UI; components/casfs/).
      ...(projMime
        ? [{
            id: 'project',
            label: t('workspace.panels.library.projectFiles'),
            icon: <FolderCog className="h-4 w-4" />,
            onSelect: () => openProjectDialog(row.id),
          } as ContextMenuItem]
        : []),
      // Project folders are still real folders — let the user browse the raw files.
      ...(projMime
        ? [{
            id: 'browse',
            label: t('workspace.panels.browseFiles'),
            icon: <FolderInput className="h-4 w-4" />,
            onSelect: () => enterFolder(row),
          } as ContextMenuItem]
        : []),
      ...(readOnly
        ? []
        : [{
            id: 'rename',
            label: t('workspace.panels.library.rename'),
            icon: <Pencil className="h-4 w-4" />,
            onSelect: () => void doRename(row.id, row.displayName),
          } as ContextMenuItem]),
    ];
    // Widgets and project folders get a dedicated full-screen open — the same
    // standalone view shared links land on (`/w/{id}`), so the owner previews
    // exactly what recipients see. (Default click adds an instance to the
    // canvas; this is the explicit "open it as its own page" alternative.)
    if (row.contentType === 'widget' || projMime) {
      items.push({
        id: 'open-fullscreen',
        label: t('workspace.panels.library.openFullScreen'),
        icon: <Maximize2 className="h-4 w-4" />,
        onSelect: () => openWithWidget(row),
      });
    }
    if (!readOnly) {
      items.push({
        id: 'move-to',
        label: t('workspace.panels.library.moveTo'),
        icon: <FolderInput className="h-4 w-4" />,
        onSelect: () => setMoveRows([row]),
      });
    }
    if (parentId !== null && !readOnly) {
      items.push({
        id: 'move-up',
        label: t('workspace.panels.library.moveUp'),
        icon: <FolderInput className="h-4 w-4 rotate-180" />,
        onSelect: () => {
          const parentOfParent = folderStack[folderStack.length - 2]?.id ?? null;
          void doMove(row.id, parentOfParent);
        },
      });
    }
    // Share is offered for both files and folders. Folders cascade their
    // visibility to owned descendants on save — the dialog surfaces a
    // preview banner with descendantCount before commit.
    if (!readOnly) {
      items.push({
        id: 'share',
        label: t('workspace.panels.library.share'),
        icon: <Share2 className="h-4 w-4" />,
        onSelect: () => setShareTargetIds([row.id]),
        separatorAfter: !isFolder,
      });
    }
    if (!isFolder) {
      if (!readOnly) items.push({
        id: 'visibility',
        label: (
          <span className="inline-flex items-center gap-1.5">
            <span>{t('workspace.panels.library.visibility')}</span>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {t(`workspace.panels.library.visibility_${v}`)}
            </span>
          </span>
        ),
        icon:
          v === 'public' ? <Globe className="h-4 w-4" /> :
          v === 'unlisted' ? <Eye className="h-4 w-4" /> :
          <EyeOff className="h-4 w-4" />,
        onSelect: () => void doCycleVisibility(row),
      });
      items.push({
        id: 'download',
        label: t('workspace.panels.library.download'),
        icon: <Download className="h-4 w-4" />,
        onSelect: () => void doDownload(row),
        separatorAfter: true,
      });
    } else if (!readOnly) {
      // Folders export as a portable `.robproj.zip` bundle (manifest + CAS
      // blobs, CRDT-free) — drop it into My Library on any env to import.
      items.push({
        id: 'export',
        label: projMime ? t('workspace.panels.library.exportProject') : t('workspace.panels.library.exportFolder'),
        icon: <Package className="h-4 w-4" />,
        onSelect: () => doExportProject(row),
        separatorAfter: true,
      });
    }
    if (!readOnly) {
      items.push({
        id: 'delete',
        label: t('workspace.panels.library.delete'),
        icon: <Trash2 className="h-4 w-4" />,
        tone: 'danger',
        onSelect: () => void doDelete(row.id),
      });
    }
    return items.filter((it) => !(it.id === 'sep1'));
  };

  // --- selection helpers ---------------------------------------------------
  const toggleSelect = (id: string) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isSelected = (id: string) => selected.has(id);

  // --- empty hint ----------------------------------------------------------
  const emptyHint = useMemo(() => {
    if (loading) return null;
    if (rows.length > 0) return null;
    if (search) return 'No matches.';
    if (atSharedRoot) return 'Nothing shared with you yet.';
    if (parentId) return 'Empty folder.';
    return 'No content yet — upload or create a folder to get started.';
  }, [loading, rows, search, parentId, atSharedRoot]);

  // ---------------------------------------------------------------------------
  // Honest meter: the percentage is never clamped — 140% of quota reads 140%,
  // with an explicit over-limit state (only the BAR saturates at full width).
  const usedPct =
    user?.storageQuota && user.storageQuota > 0
      ? Math.round(((user.storageUsed ?? 0) / user.storageQuota) * 100)
      : null;
  const overLimit = usedPct !== null && usedPct >= 100;

  return (
    <div
      className="relative flex flex-col h-full"
      onDragEnter={onPanelDragEnter}
      onDragOver={onPanelDragOver}
      onDragLeave={onPanelDragLeave}
      onDrop={onPanelDrop}
    >
      {/* Drop overlay — shown while an OS file is dragged over the panel.
       * pointer-events-none so the drop still lands on the panel handler. */}
      {isFileDragging && (
        <div className="pointer-events-none absolute inset-0 z-30 m-1.5 flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-primary/60 bg-primary/10 backdrop-blur-sm">
          <Upload className="h-6 w-6 text-primary" />
          <div className="text-xs font-medium text-foreground">{t('workspace.panels.dropToUpload')}</div>
          <div className="text-[10px] text-muted-foreground">
            a <span className="font-mono">.robproj.zip</span> {t('workspace.panels.importsAsProject')}
          </div>
        </div>
      )}
      {/* Single chrome row: search + view-mode + "+" menu.
       * Three concerns collapsed into ~36px so the actual content
       * list owns the rest of the panel. Selection bar and
       * breadcrumbs slot in beneath only when relevant. */}
      <div className="flex items-center gap-1.5 px-2 pt-1.5 pb-1">
        {/* Shared panel search input — identical field across every view
            (autofocus + clear affordance live inside it). */}
        <PanelSearchInput
          className="flex-1"
          value={search}
          onChange={setSearch}
          placeholder={t('workspace.panels.searchFiles')}
        />
        <div className="relative shrink-0">
          <button
            type="button"
            onClick={() => setSortOpen((v) => !v)}
            className="h-8 w-8 rounded-md hover:bg-foreground/8 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
            aria-label={t('workspace.panels.sort')}
            aria-haspopup="menu"
            aria-expanded={sortOpen}
            title={t('workspace.panels.sort')}
          >
            <ArrowDownUp className="h-3.5 w-3.5" />
          </button>
          {sortOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                aria-hidden
                onClick={() => setSortOpen(false)}
              />
              <div
                role="menu"
                className="absolute right-0 top-9 z-50 min-w-[168px] rounded-lg border border-border bg-popover p-1 shadow-lg"
              >
                {(
                  [
                    { id: 'recent', label: t('workspace.panels.library.sortRecent') },
                    { id: 'newest', label: t('workspace.panels.library.sortNewest') },
                    { id: 'oldest', label: t('workspace.panels.library.sortOldest') },
                    { id: 'name', label: t('workspace.panels.library.sortName') },
                    { id: 'size', label: t('workspace.panels.library.sortSize') },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    role="menuitemradio"
                    aria-checked={sortMode === opt.id}
                    onClick={() => {
                      setSortMode(opt.id);
                      setSortOpen(false);
                    }}
                    className={cn(
                      'flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-xs text-left hover:bg-accent transition-colors',
                      sortMode === opt.id ? 'text-foreground font-medium' : 'text-muted-foreground',
                    )}
                  >
                    {opt.label}
                    {sortMode === opt.id && <Check className="h-3.5 w-3.5 shrink-0" />}
                  </button>
                ))}
                <div className="my-1 h-px bg-border" />
                <button
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={foldersFirst}
                  onClick={() => setFoldersFirst((v) => !v)}
                  className={cn(
                    'flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-xs text-left hover:bg-accent transition-colors',
                    foldersFirst ? 'text-foreground font-medium' : 'text-muted-foreground',
                  )}
                >
                  Folders first
                  {foldersFirst && <Check className="h-3.5 w-3.5 shrink-0" />}
                </button>
              </div>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={() => setLayout((l) => (l === 'flat' ? 'tree' : 'flat'))}
          className="h-8 w-8 rounded-md hover:bg-foreground/8 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors shrink-0"
          aria-label={layout === 'flat' ? t('workspace.panels.switchToFolderView') : t('workspace.panels.switchToFlatView')}
          title={layout === 'flat' ? t('workspace.panels.folderView') : t('workspace.panels.flatView')}
        >
          {layout === 'flat' ? (
            <ListTree className="h-3.5 w-3.5" />
          ) : (
            <AlignJustify className="h-3.5 w-3.5" />
          )}
        </button>
        <button
          type="button"
          onClick={() => setViewMode((m) => (m === 'list' ? 'grid' : 'list'))}
          className="h-8 w-8 rounded-md hover:bg-foreground/8 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors shrink-0"
          aria-label={viewMode === 'list' ? 'Switch to grid view' : 'Switch to list view'}
          title={viewMode === 'list' ? t('workspace.panels.gridView') : t('workspace.panels.listView')}
        >
          {viewMode === 'list' ? (
            <LayoutGrid className="h-3.5 w-3.5" />
          ) : (
            <Rows3 className="h-3.5 w-3.5" />
          )}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="sr-only"
          onChange={(e) => {
            if (e.target.files?.length) void onUploadFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {/* Create menu — opened by the panel header's "+" (which lives in the
       * left-panel header, the SAME slot as every other view's create
       * button) via the `requestContentCreate` event. Anchored to the
       * panel's top-right so it drops down right under that "+". */}
      {/* New folder / Upload both write to YOUR library, which the shared
       * branch isn't showing — so the menu is suppressed there. */}
      {plusOpen && !inShared && (
        <>
          <div
            aria-hidden
            className="fixed inset-0 z-40"
            onClick={() => setPlusOpen(false)}
          />
          <div
            role="menu"
            className="glass-panel absolute right-2 top-1 z-50 w-44 rounded-lg py-1 text-sm shadow-2xl"
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setPlusOpen(false);
                onNewFolder();
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-foreground/8 transition-colors text-left"
            >
              <FolderPlus className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs">{t('workspace.panels.newFolder')}</span>
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setPlusOpen(false);
                fileInputRef.current?.click();
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-foreground/8 transition-colors text-left"
            >
              <Upload className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs">{t('workspace.panels.uploadFiles')}</span>
            </button>
          </div>
        </>
      )}

      {/* Kind chips — root only. Registry-backed filters over what a row IS
       * (app document / workspace / file), not its media type. "Shared" is
       * the same virtual folder as the pinned row, one tap closer. */}
      {folderStack.length === 1 && !inShared && (
        <div className="flex items-center gap-1 overflow-x-auto px-2 pb-1">
          {(
            [
              { id: 'all', label: t('workspace.panels.library.filterAll') },
              { id: 'document', label: t('workspace.panels.library.filterDocuments') },
              { id: 'workspace', label: t('workspace.panels.library.filterWorkspaces') },
              { id: 'file', label: t('workspace.panels.library.filterFiles') },
            ] as const
          ).map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setKindFilter(c.id)}
              aria-pressed={kindFilter === c.id}
              className={panelChip(kindFilter === c.id)}
            >
              {c.label}
            </button>
          ))}
          {sharedCount > 0 && (
            <button type="button" onClick={enterShared} className={panelChip(false)}>
              Shared
            </button>
          )}
        </div>
      )}

      {/* Breadcrumbs — only rendered when the user has descended into
       * a subfolder. At root the row is suppressed entirely so the
       * panel has zero filler chrome. */}
      {folderStack.length > 1 && (
        <div className="flex items-center gap-0.5 overflow-x-auto px-2.5 pb-1 text-[11px] text-muted-foreground">
          {folderStack.map((c, i) => (
            <span key={`${c.id ?? 'root'}-${i}`} className="inline-flex items-center gap-0.5 shrink-0">
              <button
                type="button"
                onClick={() => jumpToCrumb(i)}
                className={cn(
                  'truncate max-w-[110px] hover:text-foreground transition-colors',
                  i === folderStack.length - 1 && 'text-foreground font-medium',
                )}
              >
                {c.name}
              </button>
              {i < folderStack.length - 1 && <ChevronRight className="h-3 w-3 opacity-60" />}
            </span>
          ))}
        </div>
      )}

      {/* Selection bar — only when items are checked. Replaces the
       * FilterRail's bulk-action slot we dropped together with chips. */}
      {selected.size > 0 && (
        <div className="mx-2 mb-1 flex items-center gap-2 rounded-md bg-foreground/8 px-2 py-1 text-[11px]">
          <span className="tabular-nums font-medium">{selected.size} selected</span>
          {/* Share is the primary bulk action — applies visibility +
           * grants to the whole selection in one save. Items the user
           * doesn't own are silently skipped server-side, so in the shared
           * branch (where nothing is theirs) both actions are hidden. */}
          {!inShared && (
            <>
              <button
                type="button"
                onClick={() => {
                  const rowsById = new Map(rows.map((r) => [r.id, r]));
                  const picked = Array.from(selected)
                    .map((id) => rowsById.get(id))
                    .filter((r): r is ContentRow => !!r);
                  if (picked.length) setMoveRows(picked);
                }}
                className="ml-auto inline-flex items-center gap-1 h-6 px-1.5 rounded hover:bg-foreground/8 transition-colors"
                data-testid="my-library-bulk-move"
              >
                <FolderInput className="h-3 w-3" />
                Move
              </button>
              <button
                type="button"
                onClick={() => setShareTargetIds(Array.from(selected))}
                className="inline-flex items-center gap-1 h-6 px-1.5 rounded hover:bg-foreground/8 transition-colors"
                data-testid="my-library-bulk-share"
              >
                <Share2 className="h-3 w-3" />
                Share
              </button>
              <button
                type="button"
                onClick={() => void doBulkDelete()}
                className="inline-flex items-center gap-1 h-6 px-1.5 rounded text-rose-600 dark:text-rose-400 hover:bg-foreground/8 transition-colors"
              >
                <Trash2 className="h-3 w-3" />
                Delete
              </button>
            </>
          )}
          <button
            type="button"
            onClick={clearSelection}
            className={cn('h-6 px-1.5 rounded hover:bg-foreground/8 transition-colors', inShared && 'ml-auto')}
          >
            Clear
          </button>
        </div>
      )}

      {/* Body */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto">
        {/* Shared with me — a virtual folder pinned above your own files.
         * Root only, and only once somebody has actually shared something.
         *
         * The hairline under it and the "My files" label below are one
         * thing (2026-09-21): without them the pinned row, a navigation
         * button with a count and a chevron, sat directly on top of the All
         * list, which is `owner=me`, and read as a section header over an
         * empty section with one unrelated list under it. A person told
         * their share was "under Shared with me" looked at the rows beneath
         * the row and found nothing, because nothing shared can ever be
         * there. The label names what the list below actually is, and it is
         * drawn only when the pinned row is, so a root nobody has shared
         * into keeps its zero-filler chrome. */}
        {folderStack.length === 1 && !search && sharedCount > 0 && (
          <>
            <button
              type="button"
              onClick={enterShared}
              data-testid="content-shared-with-me"
              className="flex w-full items-center gap-2 border-b border-border/60 px-3 py-2 text-left transition-colors hover:bg-accent/50"
            >
              <Users className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.7} />
              <span className="flex-1 truncate text-[13px] font-medium">{t('workspace.panels.sharedWithMe')}</span>
              <span className="tabular-nums text-[11px] text-muted-foreground">{sharedCount}</span>
              <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            </button>
            <div
              data-testid="content-my-files-section"
              className="px-3 pt-2.5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground select-none"
            >
              {t('workspace.files.myFiles')}
            </div>
          </>
        )}
        {loading && (
          <div className="px-3 py-4 text-xs text-muted-foreground">Loading…</div>
        )}
        {!loading && emptyHint && (
          <div className="px-3 py-6 text-xs text-muted-foreground select-none">
            {emptyHint}
          </div>
        )}
        {!loading && rows.length > 0 && viewMode === 'grid' && (
          <GridView
            rows={rows}
            selected={selected}
            dropTargetId={dropTargetId}
            ctxBind={ctx.bindTrigger}
            onClick={openRow}
            onDragStart={onRowDragStart}
            onFolderDragOver={onFolderDragOver}
            onFolderDragLeave={onFolderDragLeave}
            onFolderDrop={onFolderDrop}
            onToggleSelect={toggleSelect}
            isSelected={isSelected}
            onCanvasCount={onCanvasCount}
          />
        )}
        {!loading && rows.length > 0 && viewMode === 'list' && (
          <ListView
            rows={rows}
            selected={selected}
            dropTargetId={dropTargetId}
            ctxBind={ctx.bindTrigger}
            onClick={openRow}
            onDragStart={onRowDragStart}
            onFolderDragOver={onFolderDragOver}
            onFolderDragLeave={onFolderDragLeave}
            onFolderDrop={onFolderDrop}
            onToggleSelect={toggleSelect}
            isSelected={isSelected}
            onCanvasCount={onCanvasCount}
          />
        )}
        {!loading && hasMore && <div ref={sentinelRef} aria-hidden className="h-4" />}
        {loadingMore && (
          <div className="flex justify-center py-2">
            <Spinner size="sm" tone="muted" />
          </div>
        )}
      </div>

      {/* Storage footer. One-line text + hair-thin progress bar; the
       * old big block above the action row took ~40px of prime real
       * estate for info the user only checks occasionally. Tinted red
       * past 90% so a near-full quota still grabs attention. */}
      {usedPct !== null && user?.storageQuota && (
        <div className="shrink-0 px-3 py-1.5 text-[10px] text-muted-foreground select-none">
          <div
            className={cn(
              'flex items-center justify-between tabular-nums',
              overLimit && 'font-medium text-red-600 dark:text-red-400',
            )}
          >
            <span>
              {formatBytes(user.storageUsed ?? 0)} of {formatBytes(user.storageQuota)}
              {overLimit ? ' · over limit' : ''}
            </span>
            <span>{usedPct}%</span>
          </div>
          <div className="mt-1 h-0.5 w-full rounded-full bg-foreground/8 overflow-hidden">
            <div
              className={cn(
                'h-full transition-all',
                usedPct >= 90 ? 'bg-red-500' : usedPct >= 70 ? 'bg-amber-500' : 'bg-foreground/40',
              )}
              style={{ width: `${Math.min(100, usedPct)}%` }}
            />
          </div>
          {overLimit && (
            <div className="mt-1">{t('workspace.panels.savesPaused')}</div>
          )}
          {usedPct >= 90 && (
            <div className="mt-1 flex items-center gap-2.5">
              <button
                type="button"
                onClick={manageStorage}
                className="font-medium text-primary hover:underline"
              >
                {t('workspace.panels.manageStorage')}
              </button>
              <button
                type="button"
                onClick={() => openPlansDialog()}
                className="font-medium text-primary hover:underline"
              >
                Upgrade
              </button>
            </div>
          )}
        </div>
      )}

      {/* Right-click + long-press context menu */}
      <FileContextMenu
        anchor={ctx.anchor}
        onClose={ctx.close}
        items={ctx.target ? buildMenuItems(ctx.target) : []}
      />

      {/* "Move to…" — the SAME PortalFileDialog as Open/Save (move mode), so the
       * three file dialogs share one look + one browser. Handles one row (the
       * context menu) or the whole selection (the bulk bar). Modal portal →
       * sits above panels / FABs / canvas regardless of where it was invoked. */}
      {moveRows && moveRows.length > 0 && (
        <PortalFileDialog
          mode="move"
          title={
            moveRows.length === 1
              ? `Move ‘${moveRows[0].displayName}’`
              : `Move ${moveRows.length} items`
          }
          excludeIds={moveRows.map((r) => r.id)}
          initialFolderId={parentId}
          onResolve={async (res) => {
            const targets = moveRows;
            setMoveRows(null);
            if (!res || !targets) return;
            for (const t of targets) {
              // Sequential: each move is optimistic locally; the server's
              // subtree cycle guard rejects any pathological member and the
              // failure path refreshes the view.
              await doMove(t.id, res.parentId);
            }
            clearSelection();
          }}
        />
      )}

      {shareTargetIds && (
        <ShareDialog
          kind="content"
          contentIds={shareTargetIds}
          onClose={() => setShareTargetIds(null)}
          onSaved={() => {
            void refresh();
            clearSelection();
          }}
        />
      )}

      {/* File lightbox — the click-to-open surface for files (like post
       * media). Arrows/swipe walk the loaded listing in its sort order; the
       * header carries Download + the registry-backed "Open in" menu. */}
      {previewIndex >= 0 && (
        <MediaLightbox
          items={previewItems}
          initialIndex={previewIndex}
          onClose={() => {
            setPreviewId(null);
            setOpenInMenu(false);
          }}
          onIndexChange={(i) => {
            const r = previewRows[i];
            if (!r) return;
            setPreviewId(r.id);
            setOpenInMenu(false);
            loadHandlers(r.id);
          }}
          headerLeft={({ item }) => {
            const row = previewRows.find((r) => r.id === item.contentId);
            if (!row) return null;
            const k = resolveContentKind(row);
            return (
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-white">{row.displayName}</div>
                <div className="truncate text-[11px] text-white/50">
                  {k.label}
                  {row.size ? ` · ${formatBytes(Number(row.size))}` : ''}
                </div>
              </div>
            );
          }}
          headerActions={({ item }) => {
            const row = previewRows.find((r) => r.id === item.contentId);
            if (!row) return null;
            const handlers = handlersById[row.id] ?? [];
            return (
              <>
                <button
                  type="button"
                  onClick={() => void doDownload(row)}
                  aria-label="Download"
                  title="Download"
                  className="flex h-9 w-9 items-center justify-center rounded-full text-white/60 transition-colors hover:bg-white/10"
                >
                  <Download className="h-4 w-4" />
                </button>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setOpenInMenu((v) => !v)}
                    className="inline-flex h-8 items-center gap-1 rounded-full bg-white/10 px-3 text-xs font-medium text-white transition-colors hover:bg-white/20"
                  >
                    Open in
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                  {openInMenu && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setOpenInMenu(false)} />
                      <div className="absolute right-0 top-full z-20 mt-1.5 min-w-52 rounded-xl border border-white/10 bg-zinc-900 py-1 shadow-2xl">
                        {handlers.map((h) => (
                          <button
                            key={h.id}
                            type="button"
                            onClick={() => void openWithApp(row, h)}
                            className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs text-white/90 transition-colors hover:bg-white/10"
                          >
                            <span className="text-white/70">{handlerMark(h)}</span>
                            <span className="min-w-0 flex-1 truncate">{h.name}</span>
                            {h.isDefault && <span className="text-[10px] text-white/40">Default</span>}
                          </button>
                        ))}
                        {handlers.length > 0 && <div className="mx-2 my-1 h-px bg-white/10" />}
                        <button
                          type="button"
                          onClick={() => {
                            setPreviewId(null);
                            setOpenInMenu(false);
                            void focusOrAdd(row);
                          }}
                          className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs text-white/90 transition-colors hover:bg-white/10"
                        >
                          <span className="text-white/70"><ExternalLink className="h-4 w-4" /></span>
                          {t('workspace.canvas.actions.addToCanvas')}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setOpenInMenu(false);
                            openWithWidget(row);
                          }}
                          className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs text-white/90 transition-colors hover:bg-white/10"
                        >
                          <span className="text-white/70"><Maximize2 className="h-4 w-4" /></span>
                          Open full screen
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </>
            );
          }}
          renderBody={(it) => {
            // Native media (image/video/audio/3D) falls through to the
            // lightbox's own renderers; documents get a sheet or a kind card.
            if (it.type !== 'document') return undefined;
            const row = previewRows.find((r) => r.id === it.contentId);
            if (!row) return undefined;
            const active = row.id === previewId;
            const k = resolveContentKind(row);
            if (active && sheetRenderable(row.mimeType)) {
              return (
                <div
                  className="flex h-full w-full items-stretch justify-center px-3 pb-6 pt-20 md:px-10"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="h-full w-full max-w-4xl overflow-hidden rounded-xl bg-background shadow-2xl">
                    <ContentViewer
                      contentUrl={`/api/content/${row.id}`}
                      mimeType={row.mimeType}
                      filename={row.displayName}
                      contentId={row.id}
                      className="h-full"
                      maxHeight="100%"
                    />
                  </div>
                </div>
              );
            }
            // No inline renderer (docx, archives, …) — identity card; the
            // header's Open in / Download carry the actions. Inactive
            // neighbour slides use the same card as their cheap placeholder.
            const { Icon } = kindVisual(k);
            return (
              <div className="flex h-full w-full items-center justify-center p-6">
                <div className="flex max-w-sm flex-col items-center gap-3 text-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/10 text-white/80">
                    <Icon className="h-8 w-8" />
                  </div>
                  <div className="max-w-full truncate text-sm font-medium text-white">{row.displayName}</div>
                  <div className="text-xs text-white/50">
                    {k.label}
                    {row.size ? ` · ${formatBytes(Number(row.size))}` : ''}
                  </div>
                </div>
              </div>
            );
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub views
// ---------------------------------------------------------------------------

type ViewProps = {
  rows: ContentRow[];
  selected: Set<string>;
  dropTargetId: string | null;
  ctxBind: ReturnType<typeof useFileContextMenu<ContentRow>>['bindTrigger'];
  onClick: (row: ContentRow) => void;
  onDragStart: (e: ReactDragEvent, row: ContentRow) => void;
  onFolderDragOver: (e: ReactDragEvent, folderId: string) => void;
  onFolderDragLeave: (folderId: string) => void;
  onFolderDrop: (e: ReactDragEvent, folder: ContentRow) => void;
  onToggleSelect: (id: string) => void;
  isSelected: (id: string) => boolean;
  onCanvasCount: (refId: string) => number;
};

function ListView({
  rows,
  dropTargetId,
  ctxBind,
  onClick,
  onDragStart,
  onFolderDragOver,
  onFolderDragLeave,
  onFolderDrop,
  onToggleSelect,
  isSelected,
  onCanvasCount,
}: ViewProps) {
  // Defensive de-dupe by id: the content list can occasionally carry a
  // duplicate row (e.g. a folder reachable via two ancestors), which would
  // collide on the React key and warn. Keep first occurrence.
  const seenIds = new Set<string>();
  const uniqueRows = rows.filter((r) => (seenIds.has(r.id) ? false : (seenIds.add(r.id), true)));
  return (
    <ul className="px-1 py-1 space-y-0.5">
      {uniqueRows.map((row) => {
        const isFolder = row.contentType === 'folder';
        const isDropTarget = isFolder && dropTargetId === row.id;
        const checked = isSelected(row.id);
        const count = onCanvasCount(row.id);
        return (
          <li key={row.id}>
            <div
              role="button"
              tabIndex={0}
              draggable
              onDragStart={(e) => onDragStart(e, row)}
              onDragOver={isFolder ? (e) => onFolderDragOver(e, row.id) : undefined}
              onDragLeave={isFolder ? () => onFolderDragLeave(row.id) : undefined}
              onDrop={isFolder ? (e) => onFolderDrop(e, row) : undefined}
              onClick={() => onClick(row)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick(row);
                }
              }}
              {...ctxBind(row)}
              className={cn(
                'group w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left transition-colors cursor-default',
                'hover:bg-foreground/6 focus:outline-none focus:bg-foreground/6',
                checked && 'bg-foreground/8',
                isDropTarget && 'bg-amber-500/15 ring-1 ring-amber-500/40',
              )}
            >
              <SelectionDot
                checked={checked}
                onChange={() => onToggleSelect(row.id)}
              />
              <ContentPreview item={row} size="sm" />
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium truncate">{row.displayName}</div>
                <RowKindLine row={row} />
              </div>
              {(row.effectiveVisibility ?? 'private') !== 'private' || (row.grantCount ?? 0) > 0 ? (
                <VisibilityBadge
                  visibility={row.effectiveVisibility ?? 'private'}
                  source={row.visibilitySource ?? 'self'}
                  sourceName={row.visibilitySourceName ?? null}
                  grantCount={row.grantCount ?? 0}
                />
              ) : null}
              {count > 0 && !isFolder && (
                <span
                  title={`${count} on canvas`}
                  className="shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-foreground/12 font-semibold"
                >
                  {count}
                </span>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function GridView({
  rows,
  dropTargetId,
  ctxBind,
  onClick,
  onDragStart,
  onFolderDragOver,
  onFolderDragLeave,
  onFolderDrop,
  onToggleSelect,
  isSelected,
  onCanvasCount,
}: ViewProps) {
  return (
    <div className="grid grid-cols-2 gap-1.5 p-2">
      {rows.map((row) => {
        const isFolder = row.contentType === 'folder';
        const isDropTarget = isFolder && dropTargetId === row.id;
        const checked = isSelected(row.id);
        const count = onCanvasCount(row.id);
        return (
          <div
            key={row.id}
            role="button"
            tabIndex={0}
            draggable
            onDragStart={(e) => onDragStart(e, row)}
            onDragOver={isFolder ? (e) => onFolderDragOver(e, row.id) : undefined}
            onDragLeave={isFolder ? () => onFolderDragLeave(row.id) : undefined}
            onDrop={isFolder ? (e) => onFolderDrop(e, row) : undefined}
            onClick={() => onClick(row)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick(row);
              }
            }}
            {...ctxBind(row)}
            className={cn(
              'group relative flex flex-col rounded-lg p-1 transition-colors cursor-default',
              'hover:bg-foreground/6 focus:outline-none focus:bg-foreground/6',
              checked && 'bg-foreground/10',
              isDropTarget && 'bg-amber-500/20 ring-1 ring-amber-500/40',
            )}
          >
            <ContentPreview item={row} size="tile" />
            <div className="px-1 pt-1">
              <div className="text-[11px] font-medium truncate">{row.displayName}</div>
              <RowKindLine row={row} />
              {(row.effectiveVisibility ?? 'private') !== 'private' || (row.grantCount ?? 0) > 0 ? (
                <div className="mt-0.5">
                  <VisibilityBadge
                    visibility={row.effectiveVisibility ?? 'private'}
                    source={row.visibilitySource ?? 'self'}
                    sourceName={row.visibilitySourceName ?? null}
                    grantCount={row.grantCount ?? 0}
                  />
                </div>
              ) : null}
            </div>
            <div className="absolute top-1.5 left-1.5">
              <SelectionDot
                checked={checked}
                onChange={() => onToggleSelect(row.id)}
              />
            </div>
            {count > 0 && !isFolder && (
              <span
                title={`${count} on canvas`}
                className="absolute top-1.5 right-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-background/85 backdrop-blur font-semibold shadow-sm"
              >
                {count}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** The kind line: identity from the registry, never a raw MIME. App documents
 *  name their app (tinted to match the icon); files add a human kind + size. */
function RowKindLine({ row }: { row: ContentRow }) {
  const resolved = resolveContentKind(row);
  const size = resolved.kind === 'file' && row.size ? formatBytes(Number(row.size)) : null;
  return (
    <div className="text-[10px] text-muted-foreground truncate">
      {resolved.kind === 'project' && resolved.app ? (
        <span className="font-medium">{resolved.label}</span>
      ) : (
        resolved.label
      )}
      {size ? ` · ${size}` : ''}
    </div>
  );
}

function SelectionDot({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: () => void;
}) {
  const stop = (e: React.MouseEvent | ReactPointerEvent) => {
    e.stopPropagation();
  };
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={(e) => {
        stop(e);
        onChange();
      }}
      onPointerDown={stop}
      className={cn(
        'shrink-0 inline-flex h-4 w-4 items-center justify-center rounded-full transition-all',
        checked
          ? 'bg-foreground text-background shadow-sm'
          : 'bg-foreground/10 text-transparent group-hover:bg-foreground/20',
      )}
    >
      <svg viewBox="0 0 12 12" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M2.5 6.5l2.5 2.5 4.5-5.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}
